// src/components/RecordPlayer/PlayerControls.jsx
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaPlay, FaPause, FaStepBackward, FaStepForward, FaClock } from 'react-icons/fa';
import { formatTime, formatDateTime, formatHourMinute } from '../../utils/timeUtils';
import { isIOS, isAndroid, isHarmonyOS } from '../../utils/browserUtils';
import { getNearestPresentationNode, filterPresentationTimeNodes } from '../../utils/presentationUtils';
import VolumeControl from '../common/VolumeControl';
import VolumeControlMobile from '../common/VolumeControlMobile';
import ProgressBar from '../common/ProgressBar';
import { getPlaybackMemory, savePlaybackPosition } from '../../utils/playMemoryUtils'

const PlayerControls = ({
  roomid,
  currentTime,
  sessionInfo,
  audioInfo,
  duration,
  isMobile,
  onSeek,
  playbackRate,
  isPlaying,
  onPlayPause,
  onBackward,
  onForward,
  onChangePlaybackRate,
  speedOptions = [0.5, 1, 1.5, 2],
  audioOffset = 0,
  videoOffset = 0,
  isMediaReady,
  onAudioStateChange,
  presentationTimeNodes = [] // 新增：课件时间节点
}) => {

  const audioRef = useRef(null);
  
  // 音量控制
  const [volume, setVolume] = useState(1); // 默认音量100%
  const [prevVolume, setPrevVolume] = useState(1); // 用于保存静音前的音量

  // 状态管理
  const [isAudioReady, setIsAudioReady] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  // const [isMobile, setIsMobile] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioDuration, setAudioDuration] = useState(-1);
  const [isFirstPlay, setIsFirstPlay] = useState(true);
  const [memoryPlay, setMemoryPlay] = useState(false);
  const [currentPresentation, setCurrentPresentation] = useState(null); // 新增：当前课件信息
  const [filteredPresentationTimeNodes, setFilteredPresentationTimeNodes] = useState([]); // 新增：过滤后的课件时间节点
  const lastPlayMemoryTime = useRef(null);

  const isRealyMobile = useMemo(() => {
    return isAndroid() || isIOS() || isHarmonyOS();
  }, []);

  // 获取记忆播放位置
  useEffect(() => { 

    // 获取记忆播放位置
    const memoryPosition = getPlaybackMemory(roomid)
    if(memoryPosition && memoryPosition.position && memoryPosition.position !== 0) {
      setMemoryPlay(true);
      handleSeek(memoryPosition.position);
    }
  }, []);

  // 监听当前时间变化，更新当前课件信息
  useEffect(() => {
    if (presentationTimeNodes && presentationTimeNodes.length > 0) {
      const nearestNode = getNearestPresentationNode(presentationTimeNodes, currentTime * 1000);
      if (nearestNode && (!currentPresentation || nearestNode.time !== currentPresentation.time)) {
        setCurrentPresentation(nearestNode);
      }
    }
  }, [currentTime, presentationTimeNodes, currentPresentation]);

  // 过滤课件时间节点
  useEffect(() => {
    if (presentationTimeNodes && presentationTimeNodes.length > 0 && duration > 0) {
      const filtered = filterPresentationTimeNodes(presentationTimeNodes, duration);
      setFilteredPresentationTimeNodes(filtered);
    } else {
      setFilteredPresentationTimeNodes([]);
    }
  }, [presentationTimeNodes, duration]);

  useEffect(() => { 
    if(memoryPlay) {
      // 2秒钟后消失
      setTimeout(() => {
        setMemoryPlay(false);
      }, 2900);
    }
  }, [memoryPlay]);

  useEffect(() => { 

    // 如果不在音频播放范围，则将播放位置清空
    if(currentTime < audioOffset || (audioDuration > 0 && currentTime > audioDuration + audioOffset - 300)) {
      savePlaybackPosition(roomid, 0);
      lastPlayMemoryTime.current = null;
    } else { 
      // 每秒钟更新一次记录
      if(!lastPlayMemoryTime.current || Math.abs(currentTime - lastPlayMemoryTime.current) > 1) {
        savePlaybackPosition(roomid, currentTime);
        lastPlayMemoryTime.current = currentTime;
      }
    }
  }, [currentTime]);

  // 是否到了音频可以播放的时间
  const [isAudioPlayable, setIsAudioPlayable] = useState(false);

  const inactivityTimer = useRef(null);
  const speedMenuRef = useRef(null);

  const audioLogsTextElement = document.getElementById('cache-logs-text');
  const updateLog = (log) => {
    if (audioLogsTextElement) {
      audioLogsTextElement.innerHTML += `[${formatTime(currentTime)}]${log} <br>`;
    } else {
      console.log(`[${formatTime(currentTime)}]${log}`);
    }
  };

  // 确保播放速率始终为1.0
  const ensureNormalPlaybackRate = () => {
    if (audioRef.current) {
      if(audioRef.current.playbackRate !== playbackRate) {
        audioRef.current.playbackRate = playbackRate;
      }
    }
  };

  // 根据时间变化，解析音频URl和偏移时间，进行初始化
  useEffect(() => {
    // 如果当前时间大于等于 audioInfo.milliseconds，则更新 audioUrl, audioOffset
    if (currentTime + 1 >= audioOffset) {
      setAudioUrl(audioInfo.url);
    }
  }, [audioInfo, currentTime]);

  useEffect(() => {
    
    if (audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.load();
    }
  }, [audioUrl]);

  useEffect(() => {
    if (!audioRef.current) return;
    
    // 计算音频应该播放的时间点
    const audioPlayTime = currentTime - audioOffset;
    
    if(currentTime < audioOffset || (audioDuration > 0 && currentTime > audioDuration + audioOffset)) {
      setIsAudioPlayable(false);
    } else {
      setIsAudioPlayable(true);
    }

    // 如果应该播放但音频还未开始
    if (isPlaying) {
      if(audioPlayTime >= 0 && currentTime < audioOffset + audioDuration) {
        
        // 确保正常播放速率
        ensureNormalPlaybackRate();

        const audioActualPlayTime = audioRef.current.currentTime;
        const audioPlayOffset = audioActualPlayTime - audioPlayTime;
        // document.getElementById('audio-play-offset').textContent = `a: ${audioActualPlayTime}-${audioPlayTime}-${audioPlayOffset}`;

        // 如果音频时间落后于应该播放的时间超过0.5秒，跳转
        if (Math.abs(audioPlayOffset) > 0.05) {
          if(Math.abs(audioPlayOffset) < 1) {
            onSeek(audioPlayTime + audioOffset);
            resetInactivityTimer();
          }

          if(Math.abs(audioPlayOffset) >= 1) {
            audioRef.current.currentTime = audioPlayTime;
          }
        }
      }
    }
  }, [currentTime, isPlaying, audioOffset, playbackRate, audioDuration]);

  // 时间自然流逝，导致时间轴位置改变，自动播放
  useEffect(() => { 
    if(isAudioPlayable && isPlaying) { 
      // 播放音频
      handleAudioPlay();
    }
  }, [isAudioPlayable]);

  // 设备检测
  // useEffect(() => {
  //   const checkMobile = () => {
  //     setIsMobile(window.innerWidth <= 768);
  //   };
    
  //   checkMobile();
  //   window.addEventListener('resize', checkMobile);
  //   return () => window.removeEventListener('resize', checkMobile);
  // }, []);

  // PC端自动隐藏逻辑
  useEffect(() => {
    if (isMobile) return;

    const handleActivity = () => {
      setIsVisible(true);
      resetInactivityTimer();
    };

    resetInactivityTimer();
    document.addEventListener('mousemove', handleActivity);
    document.addEventListener('keydown', handleActivity);
    document.addEventListener('touchstart', handleActivity);

    return () => {
      clearTimeout(inactivityTimer.current);
      document.removeEventListener('mousemove', handleActivity);
      document.removeEventListener('keydown', handleActivity);
      document.removeEventListener('touchstart', handleActivity);
    };
  }, [isMobile]);

  // 音量控制
  // 更新audio元素的音量
  useEffect(() => {
    if (audioRef.current) {
      updateLog(`prevolume: ${audioRef.current.muted}, volume: ${volume}`);
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // 处理音量变化
  const handleVolumeChange = (value) => {

    const newVolume = parseFloat(value);
    setVolume(newVolume);
    
    // 如果从静音状态调整音量，更新prevVolume
    if (newVolume > 0 && prevVolume === 0) {
      setPrevVolume(newVolume);
    }
  };

  // 点击外部关闭速度菜单
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(e.target)) {
        setShowSpeedMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => { 

    console.log('[AudioPlayer]isAudioReady', isAudioReady);

    onAudioStateChange(isAudioReady)

  }, [isAudioReady]);

  useEffect(() => { 
    
    if (!isMediaReady) {
      if(!audioRef.current.paused) {
        handleAudioPause()
      }
      return;
    };

    if (isPlaying) {
      if(audioRef.current.paused) {
        // 播放
        handleAudioPlay()
      }
    } else {
      // 暂停
      handleAudioPause()
    }
  }, [isMediaReady]);

  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      if (!isMobile) setIsVisible(false);
    }, 3000);
  };

  const handleSeek = (newTime) => {

    if (newTime - audioOffset >= 0) {
      audioRef.current.currentTime = newTime - audioOffset;
    } else {
      audioRef.current.currentTime = 0;
      handleAudioPause();
    }

    onSeek(newTime);
    resetInactivityTimer();
  };

  const handleBackward = useCallback(() => { 
    if (currentTime - 15 - audioOffset < 0) {
      audioRef.current.currentTime = 0;
      handleAudioPause();
    }
    onBackward();
  }, [currentTime, audioOffset, onBackward]);

  const handleForward = useCallback(() => { 
    if (currentTime + 15 - audioOffset < 0) {
      audioRef.current.currentTime = 0;
      handleAudioPause();
    }
    onForward();
  }, [currentTime, audioOffset, onForward]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      handleAudioPause()
    } else {
      handleAudioPlay()
    }
    onPlayPause();
    resetInactivityTimer();
  }, [isPlaying, onPlayPause, resetInactivityTimer]);

  // 使用 useCallback 包裹键盘事件处理
  const handleKeyDown = useCallback((event) => {
    // 忽略输入框中的按键
    const activeElement = document.activeElement;
    const isInputFocused = 
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA';
    
    if (isInputFocused) return;

    const { key } = event;
    switch (key) {
      case ' ':
        event.preventDefault();
        handlePlayPause();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        handleBackward();
        break;
      case 'ArrowRight':
        event.preventDefault();
        handleForward();
        break;
      default:
        break;
    }
  }, [handlePlayPause, handleBackward, handleForward]);

  // 绑定键盘事件
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleAudioPlay = () => { 

    try {

      if (!audioRef.current) return;

      // 如果当前时间 小于 音频开始播放时间，则返回
      if (currentTime < audioOffset || (audioDuration > 0 && currentTime > audioDuration + audioOffset)) return;

      const promise = audioRef.current.play();
      if (promise !== undefined) {
        promise.then(() => {
          updateLog('音频播放成功');
        }).catch(error => {
          // 捕获 play() 被中断的错误
          if (error.name === 'AbortError') {
            updateLog('播放被中断（例如调用了 pause）' + error);
          } else {
            updateLog('播放失败:' + error);
          }
        }
      )}
    } catch (error) {
      updateLog('Error playing audio:' + error);
    }
  };

  const handleAudioPause = () => { 

    updateLog('[AudioPlayer] handleAudioPause');

    try {
      if (!audioRef.current) return;
      audioRef.current.pause();
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const handleSpeedChange = (speed) => {
    onChangePlaybackRate(speed);
    setShowSpeedMenu(false);
    resetInactivityTimer();
  };

  // 控制条样式类
  const controlsClass = `player-controls ${isMobile ? 'mobile' : 'pc'} ${isVisible ? 'visible' : 'hidden'}`;

  return (
    <div className={controlsClass}>

      {isMobile && (
        <div className="record-info-container"> 
          <div className="course-info">
            {sessionInfo.courseName}-{sessionInfo.sessionName}
          </div>
          <div className="time-info">
            {formatDateTime(sessionInfo.startTime)}~{formatHourMinute(sessionInfo.endTime)}
          </div>
        </div>
      )}
      {!isMobile && (
        <div className="record-info-container"> 
          <span className="course-info">
            {sessionInfo.courseName}-{sessionInfo.sessionName}
          </span>
          <span className="time-info">
            {formatDateTime(sessionInfo.startTime)}~{formatHourMinute(sessionInfo.endTime)}
          </span>
        </div>
      )}

      {/* 进度条 - 移动端放在上方 */}
      {isMobile && (
        <div className="progress-container">
          <div className="current-time">{formatTime(currentTime)}</div>
          
          <ProgressBar duration={duration} currentTime={currentTime} audioOffset={audioOffset} memoryPlay={memoryPlay} presentationTimeNodes={filteredPresentationTimeNodes} onSeek={handleSeek}/>
          
          <div className="duration">{formatTime(duration)}</div>
        </div>
      )}

      {/* 主控制区域 */}
      <div className="main-controls">
        {/* 左侧：播放控制按钮 */}
        <div className="media-controller">
          <button 
            className="control-btn" 
            onClick={handleBackward}
            aria-label="后退15秒"
          >
            <FaStepBackward />
            {!isMobile && <span>15秒</span>}
          </button>
          
          <button 
            className={`play-btn ${isPlaying ? 'playing' : ''}`}
            onClick={handlePlayPause}
            aria-label={isPlaying ? "暂停" : "播放"}
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          
          <button 
            className="control-btn" 
            onClick={handleForward}
            aria-label="前进15秒"
          >
            <FaStepForward />
            {!isMobile && <span>15秒</span>}
          </button>
        </div>
        
        {/* 中间：PC端进度条 */}
        {!isMobile && (
          <div className="progress-container">
            <div className="current-time">{formatTime(currentTime)}</div>
            <ProgressBar duration={duration} currentTime={currentTime} audioOffset={audioOffset} memoryPlay={memoryPlay} presentationTimeNodes={filteredPresentationTimeNodes} onSeek={handleSeek} />
            <div className="duration">{formatTime(duration)}</div>
          </div>
        )}
        
        {/* 右侧：倍速控制和课件信息 */}
        <div className="right-controls">

          {/* 音量按钮 */}
          {/* 判断是移动端还是PC端 */}
          {!isMobile && !isRealyMobile && (
              <VolumeControl volume={volume} onVolumeChange={handleVolumeChange} />
          )}


          {/* {!isMobile && ( */}
          <div className="speed-control" ref={speedMenuRef}>
            <button 
              className="speed-btn"
              onClick={() => setShowSpeedMenu(!showSpeedMenu)}
              aria-label="更改播放速度"
            >
              <span>倍速</span>
            </button>
            
            {showSpeedMenu && (
              <div className="speed-menu">
                {speedOptions.map(speed => (
                  <button
                    key={speed}
                    className={`speed-option ${playbackRate === speed ? 'active' : ''}`}
                    onClick={() => handleSpeedChange(speed)}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* )} */}
        </div>
      </div>

      <audio 
        ref={audioRef}
        src={audioUrl}
        preload="auto"
        autoPlay={false}
        playsInline
        webkit-playsinline="true"
        style={{ display: 'none' }}
        // 主要时间相关事件监听
        onTimeUpdate={(e) => {
            // 当播放位置改变时触发（频繁触发）
            // console.log('onTimeUpdate:', e.target.currentTime);
        }}
        onProgress={(e) => {
          // checkAudioCacheEnabled(e);
        }}
        onWaiting={(e) => {
          updateLog('[AudioPlayer] Waiting for buffering...');
          setIsAudioReady(false)
          // checkAudioCacheEnabled(e);
        }}
        onPlay={(e) => { 
          updateLog('[AudioPlayer] Play...');
        }}
        onPlaying={(e) => {
          updateLog('[AudioPlayer] Playing...');
          setIsAudioReady(true)
        }}
        onCanPlay={(e) => {
          updateLog('[AudioPlayer] Can play.');
          setIsAudioReady(true)
          // checkAudioCacheEnabled(e);
        }}
        onCanPlayThrough={(e) => { 
          updateLog('[AudioPlayer] Can play through.');
        }}
        onDurationChange={(e) => {
          updateLog("audioDurationChange：" + e.target.duration)
          setAudioDuration(e.target.duration)
        }}
        onLoadedMetadata={(e) => {
          updateLog("audioLoadedMetadata：" + e.target.duration)
          setAudioDuration(e.target.duration)
        }}
        onPause={(e) => { 
          updateLog('[AudioPlayer] Paused.');
        }}
        onAbort={(e) => { 
          updateLog('[AudioPlayer] Aborted.');
        }}
        onSeeking={(e) => {
          updateLog("audioSeeking： " + e.target.currentTime)
          // checkAudioCacheEnabled(e);
        }}
        onSeeked={(e) => {
          updateLog("audioSeeked：" + e.target.currentTime)
        }}
        onVolumeChange={(e) => {
          updateLog("audioVolumeChange：" + e.target.volume)
        }}
        onError={(e) => {
          updateLog("audioError：" + e.target.error)
        }}
      />
    </div>
  );
};

export default PlayerControls;